import type { QueryClient, QueryKey } from '@tanstack/react-query';

import { ancestorsOf, type EntityName } from './entities';
import { entityRoot, userRoleAssignmentPath } from './keys';

// Hierarchy-aware invalidation map (03-03 §2.2, CONTRACT). Encodes the containment
// cascade ONCE: mutating an entity refetches its own query tree plus every
// ancestor's (a moved device changes its lab's and institution's derived counts).
// The realtime→cache bridge (03-04) reuses this exact map, so a push and a
// mutation invalidate identically.
//
// Chain (Q-10 / HS-939): Institution → Group → Lab → Device — see `parentOf` in
// entities.ts. Mutating a device therefore also refreshes its group's counts.

/** Context a mutation hands the map — reserved for future precise (id-scoped) keys. */
export interface MutationCtx {
  id?: string;
  institutionId?: string;
  labId?: string;
  tenantId?: string;
  userId?: string;
  /**
   * HS-1141: key trees left out of the invalidation — the record a delete just removed
   * (`recordTreesOf`). Refetching it can only 404, and the default retries hold the mutation for
   * about seven seconds.
   */
  exclude?: readonly string[];
}

/**
 * A query tree: a root prefix (the root itself and every path below it), or a pattern for a tree
 * that no single prefix names exactly (HS-1565's role-assignment subtree).
 */
type QueryTree = string | RegExp;

function keyInTree(key: QueryKey, tree: QueryTree): boolean {
  const head = key[0];
  if (typeof head !== 'string') return false;
  return typeof tree === 'string' ? head === tree || head.startsWith(`${tree}/`) : tree.test(head);
}

/** Whether `key` lies in any of `trees` — the record trees a delete names (HS-1141). */
export function keyInAnyTree(key: QueryKey, trees: readonly QueryTree[]): boolean {
  return trees.some((tree) => keyInTree(key, tree));
}

function invalidateTree(
  qc: QueryClient,
  tree: QueryTree,
  exclude: readonly string[] = [],
): Promise<void> {
  return qc.invalidateQueries({
    predicate: (query) => keyInTree(query.queryKey, tree) && !keyInAnyTree(query.queryKey, exclude),
  });
}

/** Invalidate every cached query whose key path starts with `prefix`. */
export function invalidateByPrefix(qc: QueryClient, prefix: string): Promise<void> {
  return invalidateTree(qc, prefix);
}

/**
 * Query trees an entity's mutation also changes outside its parent chain. HS-1565: a user create
 * or update grants or replaces the user's base role, so the role-assignment reads go stale too.
 * Applies to every `user` mutation — a move or a delete changes those grants as well.
 */
const relatedTrees: Partial<Record<EntityName, RegExp>> = { user: userRoleAssignmentPath };

/** Distinct root prefixes to invalidate for an entity mutation: itself + ancestors. */
export function invalidationTargets(entity: EntityName): string[] {
  const roots = [entityRoot[entity], ...ancestorsOf(entity).map((a) => entityRoot[a])];
  return [...new Set(roots)];
}

export type InvalidationFn = (qc: QueryClient, ctx?: MutationCtx) => Promise<void>;

export const invalidations = Object.fromEntries(
  (Object.keys(entityRoot) as EntityName[]).map((entity) => [
    entity,
    (qc: QueryClient, ctx?: MutationCtx) =>
      Promise.all(
        [...invalidationTargets(entity), relatedTrees[entity] ?? []]
          .flat()
          .map((tree) => invalidateTree(qc, tree, ctx?.exclude)),
      ).then(() => undefined),
  ]),
) as Record<EntityName, InvalidationFn>;
